import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../utils/axiosConfig";
import localforage from "localforage";
import { assemblyConstituenciesAp } from "../components/Roles";
import PunjabLocationPicker from "./PunjabLocationPicker";
import Dashboard from "./Dashboard";
import MicDictation from "./MicDictation";
import "../css/momFormat.css";

const API_URL = process.env.REACT_APP_API_URL;
const DEFAULT_DESIGNATIONS = ["MLA", "ACI", "Mandal Leader", "ZP Chairman"];
const PUNJAB_DESIGNATIONS = [
  "Halka Pramukh",
  "Circle Incharge",
  "District Incharge",
  "MLA",
  "MP",
  "Journalist",
  "Frontal Wing",
];
const PUNJAB_PARTIES = ["AAP", "SAD", "INC", "BJP", "BSP", "Independent", "others"];
const WORD_LIMIT = 20;

const limitWords = (text, limit = WORD_LIMIT) => {
  const words = text.split(/\s+/).filter(Boolean);
  return words.length <= limit ? text : words.slice(0, limit).join(" ");
};
const wordCount = (t) => (t && t.trim() ? t.trim().split(/\s+/).length : 0);

// Append dictated/cleaned text to whatever is already in a field.
const appendDictation = (existing, added) => {
  const a = (existing || "").trim();
  const b = (added || "").trim();
  if (!a) return b;
  if (!b) return a;
  return `${a} ${b}`;
};
// last 10 digits typed by the user (strips +91 and any separators)
const phoneDigits = (p) => (p || "").replace(/\D/g, "").slice(-10);

const DynamicNewMom = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [submitting, setSubmitting] = useState(false);
  const [designations, setDesignations] = useState(DEFAULT_DESIGNATIONS);
  const [state, setState] = useState("");
  const [email, setEmail] = useState("");
  const [userName, setUserName] = useState("");
  const [gpsStatus, setGpsStatus] = useState("Locating…");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const [form, setForm] = useState({
    meetingDate: "",
    meetingTime: "",
    numStakeholders: "",
    duration: "",
    location: "",
    region: "",
    district: "",
    gMapLocation: "",
    respondentPhoto: "",
    nextMeetingDate: "",
    remarks: "",
  });
  const [stakeholders, setStakeholders] = useState([
    { name: "", designation: "", phone: "", party: "" },
  ]);
  const [discussion, setDiscussion] = useState([{ point: "", details: "" }]);
  const [stcActionables, setStcActionables] = useState([
    { issue: "", actionable: "", timeline: "" },
  ]);
  const [leaderActionables, setLeaderActionables] = useState([
    { issue: "", actionable: "", partyLeader: "", timeline: "" },
  ]);
  const [dictationLang, setDictationLang] = useState("en-IN");

  // Load user context
  useEffect(() => {
    localforage.getItem("location").then((l) => l && setState(l));
    localforage.getItem("email").then((e) => e && setEmail(e));
    localforage.getItem("userName").then((n) => n && setUserName(n));
  }, []);

  // Auto-capture GPS for new meetings
  useEffect(() => {
    if (isEdit) return;
    if (!("geolocation" in navigator)) {
      setGpsStatus("Location not supported");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setForm((p) => ({ ...p, gMapLocation: `${latitude},${longitude}` }));
        setGpsStatus("Location captured ✓");
      },
      () => setGpsStatus("Location permission denied"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [isEdit]);

  // Load existing record in edit mode
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const token = await localforage.getItem("token");
        const { data } = await api.get(`${API_URL}/mom-format/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setForm({
          meetingDate: data.meetingDate || "",
          meetingTime: data.meetingTime || "",
          numStakeholders: data.numStakeholders || "",
          duration: data.duration || "",
          location: data.location || "",
          region: data.region || "",
          district: data.district || "",
          gMapLocation: data.gMapLocation || "",
          respondentPhoto: data.respondentPhoto || "",
          nextMeetingDate: data.nextMeetingDate || "",
          remarks: data.remarks || "",
        });
        if (data.stakeholders && data.stakeholders.length)
          setStakeholders(
            data.stakeholders.map((s) => ({
              ...s,
              phone: phoneDigits(s.phone),
            }))
          );
        if (data.discussionPoints && data.discussionPoints.length)
          setDiscussion(data.discussionPoints);
        if (data.stcActionables && data.stcActionables.length)
          setStcActionables(data.stcActionables);
        if (data.leaderActionables && data.leaderActionables.length)
          setLeaderActionables(data.leaderActionables);
        // make sure any custom designations are selectable
        const custom = (data.stakeholders || [])
          .map((s) => s.designation)
          .filter((d) => d && !DEFAULT_DESIGNATIONS.includes(d));
        if (custom.length)
          setDesignations((d) => Array.from(new Set([...d, ...custom])));
        setGpsStatus(data.gMapLocation ? "Location on file ✓" : "No location");
      } catch (e) {
        alert("Could not load the meeting record.");
      }
    })();
  }, [isEdit, id]);

  const handleField = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  // ---- Stakeholders ----
  const updateStakeholder = (i, key, value) =>
    setStakeholders((rows) =>
      rows.map((r, idx) => (idx === i ? { ...r, [key]: value } : r))
    );
  const updatePhone = (i, raw) =>
    updateStakeholder(i, "phone", phoneDigits(raw));
  const handleDesignationChange = (i, value) => {
    if (value === "__add__") {
      const custom = window.prompt("Enter new designation:");
      if (custom && custom.trim()) {
        const v = custom.trim();
        setDesignations((d) => (d.includes(v) ? d : [...d, v]));
        updateStakeholder(i, "designation", v);
      }
      return;
    }
    updateStakeholder(i, "designation", value);
  };
  const addStakeholder = () =>
    setStakeholders((r) => [...r, { name: "", designation: "", phone: "", party: "" }]);
  const removeStakeholder = (i) =>
    setStakeholders((r) => (r.length > 1 ? r.filter((_, x) => x !== i) : r));

  // ---- Discussion ----
  const updateDiscussion = (i, key, value) => {
    const v = key === "point" ? limitWords(value) : value;
    setDiscussion((rows) => rows.map((r, x) => (x === i ? { ...r, [key]: v } : r)));
  };
  const addDiscussion = () => setDiscussion((r) => [...r, { point: "", details: "" }]);
  const removeDiscussion = (i) =>
    setDiscussion((r) => (r.length > 1 ? r.filter((_, x) => x !== i) : r));

  // ---- STC actionables ----
  const updateStc = (i, key, value) => {
    const v = key === "issue" ? limitWords(value) : value;
    setStcActionables((rows) => rows.map((r, x) => (x === i ? { ...r, [key]: v } : r)));
  };
  const addStc = () =>
    setStcActionables((r) => [...r, { issue: "", actionable: "", timeline: "" }]);
  const removeStc = (i) =>
    setStcActionables((r) => (r.length > 1 ? r.filter((_, x) => x !== i) : r));

  // ---- Party leader actionables ----
  const updateLeader = (i, key, value) => {
    const v = key === "issue" ? limitWords(value) : value;
    setLeaderActionables((rows) =>
      rows.map((r, x) => (x === i ? { ...r, [key]: v } : r))
    );
  };
  const addLeader = () =>
    setLeaderActionables((r) => [
      ...r,
      { issue: "", actionable: "", partyLeader: "", timeline: "" },
    ]);
  const removeLeader = (i) =>
    setLeaderActionables((r) => (r.length > 1 ? r.filter((_, x) => x !== i) : r));

  // ---- Respondent photo upload (via backend; no AWS keys in the browser) ----
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const token = await localforage.getItem("token");
      const fd = new FormData();
      fd.append("photo", file);
      const { data } = await api.post(
        `${API_URL}/mom-format/upload-photo`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setForm((p) => ({ ...p, respondentPhoto: data.url || "" }));
    } catch (err) {
      alert("Photo upload failed. Please try again (photo is optional).");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Phone validation: every filled stakeholder must have exactly 10 digits
    for (const s of stakeholders) {
      if (s.name || s.designation || s.phone) {
        if (phoneDigits(s.phone).length !== 10) {
          alert(
            `Phone number for "${s.name || "a stakeholder"}" must be exactly 10 digits (e.g. +91-7854526589).`
          );
          return;
        }
      }
    }
    if (!form.location) {
      alert("Please select the Meeting Location (Assembly Constituency).");
      return;
    }

    // Respondent (first stakeholder) name + phone are mandatory
    const firstStk = stakeholders[0] || {};
    if (!firstStk.name || !firstStk.name.trim()) {
      alert("Respondent name is required.");
      return;
    }
    if (phoneDigits(firstStk.phone).length !== 10) {
      alert("Respondent phone number is required and must be exactly 10 digits.");
      return;
    }

    setSubmitting(true);
    try {
      const token = await localforage.getItem("token");
      const cleanStakeholders = stakeholders.filter(
        (s) => s.name || s.designation || s.phone
      );
      const respondent = cleanStakeholders[0] || {};
      const payload = {
        ...form,
        state,
        email,
        createdByName: userName,
        respondentName: respondent.name || "",
        respondentDesignation: respondent.designation || "",
        respondentParty: respondent.party || "",
        stakeholders: cleanStakeholders.map((s) => ({
          ...s,
          phone: s.phone ? "+91-" + s.phone : "",
        })),
        discussionPoints: discussion.filter((d) => d.point || d.details),
        stcActionables: stcActionables.filter(
          (a) => a.issue || a.actionable || a.timeline
        ),
        leaderActionables: leaderActionables.filter(
          (a) => a.issue || a.actionable || a.partyLeader || a.timeline
        ),
      };

      if (isEdit) {
        await api.put(`${API_URL}/mom-format/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        alert("Meeting updated successfully!");
      } else {
        await api.post(`${API_URL}/mom-format/save`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        alert("Minutes of Meeting saved successfully!");
      }
      navigate("/mom-dashboard");
    } catch (err) {
      console.error("Error saving MOM:", err);
      alert(err.response?.data?.error || "Failed to save. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const designationSelect = (row, i) => {
    const opts = state === "Punjab" ? PUNJAB_DESIGNATIONS : designations;
    const known = opts.includes(row.designation);
    return (
      <select
        value={known ? row.designation : row.designation ? row.designation : ""}
        onChange={(e) => handleDesignationChange(i, e.target.value)}
      >
        <option value="">Select…</option>
        {opts.map((d) => (
          <option key={d} value={d}>
            {d}
          </option>
        ))}
        {row.designation && !known && (
          <option value={row.designation}>{row.designation}</option>
        )}
        <option value="__add__">➕ Add new designation…</option>
      </select>
    );
  };

  const partySelect = (row, i) => (
    <select
      value={row.party || ""}
      onChange={(e) => updateStakeholder(i, "party", e.target.value)}
    >
      <option value="">Select…</option>
      {PUNJAB_PARTIES.map((p) => (
        <option key={p} value={p}>
          {p}
        </option>
      ))}
    </select>
  );

  return (
    <>
      <Dashboard />
      <div className="momf-page">
        <form className="momf-form-card" onSubmit={handleSubmit}>
          <h2 className="momf-title-center">
            {isEdit ? "Update Meeting" : "Record Meeting"}
          </h2>

          {/* Section 1 */}
          <h3 className="momf-section">1. Meeting Information</h3>
          <div className="momf-grid-fields">
            <label>
              Meeting Date
              <input
                type="date"
                name="meetingDate"
                value={form.meetingDate}
                onChange={handleField}
                required
              />
            </label>
            <label>
              Meeting Time
              <input
                type="time"
                name="meetingTime"
                value={form.meetingTime}
                onChange={handleField}
                required
              />
            </label>
            <label>
              No. of Stakeholders
              <input
                type="number"
                min="0"
                name="numStakeholders"
                value={form.numStakeholders}
                onChange={handleField}
                placeholder="0"
              />
            </label>
            <label>
              Tentative Duration (hrs)
              <input
                type="number"
                min="0"
                step="0.5"
                name="duration"
                value={form.duration}
                onChange={handleField}
                placeholder="e.g. 1.5"
              />
            </label>
            {state === "Punjab" ? (
              <div className="momf-span2 pj-fullrow">
                <span className="pj-block-label">
                  Meeting Location (Assembly Constituency)
                </span>
                <PunjabLocationPicker
                  value={{
                    region: form.region,
                    district: form.district,
                    acName: form.location,
                  }}
                  onChange={({ region, district, acName }) =>
                    setForm((p) => ({
                      ...p,
                      region,
                      district,
                      location: acName,
                    }))
                  }
                />
              </div>
            ) : (
              <label className="momf-span2">
                Meeting Location (Assembly Constituency)
                <select
                  name="location"
                  value={form.location}
                  onChange={handleField}
                  required
                >
                  <option value="">Select Assembly Constituency…</option>
                  {assemblyConstituenciesAp.map((ac) => (
                    <option key={ac} value={ac}>
                      {ac}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label>
              Google Location (auto)
              <input type="text" value={form.gMapLocation} readOnly placeholder="—" />
              <span className="word-hint">{gpsStatus}</span>
            </label>
            <label>
              Respondent Photo
              <input
                type="file"
                accept="image/png, image/jpeg, image/jpg"
                onChange={handlePhotoUpload}
              />
              {uploadingPhoto && <span className="word-hint">Uploading…</span>}
              {form.respondentPhoto && (
                <a
                  className="word-hint"
                  href={form.respondentPhoto}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Photo uploaded ✓ (view)
                </a>
              )}
            </label>
          </div>

          {/* Section 2 */}
          <h3 className="momf-section">2. Stakeholders Details</h3>
          <p className="momf-note">
            The first stakeholder is treated as the meeting Respondent (shown on
            the dashboard).
          </p>
          <table className="momf-edit-table">
            <thead>
              <tr>
                <th>Stakeholder Name</th>
                <th>Designation</th>
                {state === "Punjab" && <th>Party</th>}
                <th>Phone Number (+91)</th>
                <th aria-label="actions"></th>
              </tr>
            </thead>
            <tbody>
              {stakeholders.map((s, i) => (
                <tr key={i}>
                  <td>
                    <input
                      value={s.name}
                      onChange={(e) => updateStakeholder(i, "name", e.target.value)}
                      placeholder={i === 0 ? "Respondent name" : "Name"}
                    />
                  </td>
                  <td>{designationSelect(s, i)}</td>
                  {state === "Punjab" && <td>{partySelect(s, i)}</td>}
                  <td>
                    <div className="phone-wrap">
                      <span className="phone-prefix">+91-</span>
                      <input
                        type="tel"
                        inputMode="numeric"
                        maxLength={10}
                        value={phoneDigits(s.phone)}
                        onChange={(e) => updatePhone(i, e.target.value)}
                        placeholder="Enter 10-digit number"
                      />
                    </div>
                    <span className="word-hint">
                      {phoneDigits(s.phone).length}/10 digits
                    </span>
                  </td>
                  <td className="row-actions">
                    <button type="button" onClick={() => removeStakeholder(i)} title="Remove">
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className="add-row-btn" onClick={addStakeholder}>
            + Add Stakeholder
          </button>

          {/* Section 3 */}
          <h3 className="momf-section">3. Key Points of Discussion</h3>
          <div className="dictation-lang-bar">
            <span role="img" aria-label="mic">🎤</span> Voice-to-text language:
            <select
              value={dictationLang}
              onChange={(e) => setDictationLang(e.target.value)}
            >
              <option value="en-IN">English (India)</option>
              <option value="hi-IN">Hindi</option>
              <option value="pa-IN">Punjabi</option>
              <option value="te-IN">Telugu</option>
              <option value="mr-IN">Marathi</option>
              <option value="bn-IN">Bengali</option>
              <option value="ta-IN">Tamil</option>
              <option value="kn-IN">Kannada</option>
              <option value="en-US">English (US)</option>
            </select>
            <span className="dictation-hint">
              Tap the mic in a field, speak, then “Stop &amp; Insert”. The text is
              auto-cleaned by AI.
            </span>
          </div>
          <table className="momf-edit-table">
            <thead>
              <tr>
                <th className="col-num">#</th>
                <th>Key Discussion Point (20 words)</th>
                <th>Details of Discussion</th>
                <th aria-label="actions"></th>
              </tr>
            </thead>
            <tbody>
              {discussion.map((d, i) => (
                <tr key={i}>
                  <td className="col-num">{i + 1}</td>
                  <td>
                    <input
                      value={d.point}
                      onChange={(e) => updateDiscussion(i, "point", e.target.value)}
                      placeholder="Issue / Feedback / Demand / Suggestion"
                    />
                    <span className="word-hint">
                      {wordCount(d.point)}/{WORD_LIMIT} words
                    </span>
                  </td>
                  <td>
                    <div className="ta-wrap">
                      <textarea
                        rows={2}
                        value={d.details}
                        onChange={(e) =>
                          updateDiscussion(i, "details", e.target.value)
                        }
                        placeholder="Detailed discussion…"
                      />
                      <MicDictation
                        lang={dictationLang}
                        split
                        context="discussion"
                        onResult={({ short, long }) => {
                          if (short) updateDiscussion(i, "point", limitWords(short));
                          updateDiscussion(i, "details", long);
                        }}
                        label="Dictate discussion (fills Key Point + Details)"
                      />
                    </div>
                  </td>
                  <td className="row-actions">
                    <button type="button" onClick={() => removeDiscussion(i)} title="Remove">
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className="add-row-btn" onClick={addDiscussion}>
            + Add Discussion Point
          </button>

          {/* Section 4 */}
          <h3 className="momf-section">4. Actionables — STC POC</h3>
          <table className="momf-edit-table">
            <thead>
              <tr>
                <th className="col-num">#</th>
                <th>Issue (20 words)</th>
                <th>Detailed Actionable</th>
                <th>Expected Timeline</th>
                <th aria-label="actions"></th>
              </tr>
            </thead>
            <tbody>
              {stcActionables.map((a, i) => (
                <tr key={i}>
                  <td className="col-num">{i + 1}</td>
                  <td>
                    <input
                      value={a.issue}
                      onChange={(e) => updateStc(i, "issue", e.target.value)}
                      placeholder="Issue"
                    />
                    <span className="word-hint">
                      {wordCount(a.issue)}/{WORD_LIMIT} words
                    </span>
                  </td>
                  <td>
                    <div className="ta-wrap">
                      <textarea
                        rows={2}
                        value={a.actionable}
                        onChange={(e) => updateStc(i, "actionable", e.target.value)}
                        placeholder="Detailed actionable…"
                      />
                      <MicDictation
                        lang={dictationLang}
                        split
                        context="stc_actionable"
                        onResult={({ short, long }) => {
                          if (short) updateStc(i, "issue", limitWords(short));
                          updateStc(i, "actionable", long);
                        }}
                        label="Dictate actionable (fills Issue + Actionable)"
                      />
                    </div>
                  </td>
                  <td>
                    <input
                      type="date"
                      value={a.timeline}
                      onChange={(e) => updateStc(i, "timeline", e.target.value)}
                    />
                  </td>
                  <td className="row-actions">
                    <button type="button" onClick={() => removeStc(i)} title="Remove">
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className="add-row-btn" onClick={addStc}>
            + Add STC Actionable
          </button>

          {/* Section 5 */}
          <h3 className="momf-section">5. Actionables — Party Leader</h3>
          <table className="momf-edit-table">
            <thead>
              <tr>
                <th className="col-num">#</th>
                <th>Issue / Action Point (20 words)</th>
                <th>Detailed Actionable</th>
                <th>Concerned Party Leader</th>
                <th>Expected Timeline</th>
                <th aria-label="actions"></th>
              </tr>
            </thead>
            <tbody>
              {leaderActionables.map((a, i) => (
                <tr key={i}>
                  <td className="col-num">{i + 1}</td>
                  <td>
                    <input
                      value={a.issue}
                      onChange={(e) => updateLeader(i, "issue", e.target.value)}
                      placeholder="Issue / action point"
                    />
                    <span className="word-hint">
                      {wordCount(a.issue)}/{WORD_LIMIT} words
                    </span>
                  </td>
                  <td>
                    <div className="ta-wrap">
                      <textarea
                        rows={2}
                        value={a.actionable}
                        onChange={(e) =>
                          updateLeader(i, "actionable", e.target.value)
                        }
                        placeholder="Detailed actionable…"
                      />
                      <MicDictation
                        lang={dictationLang}
                        split
                        context="leader_actionable"
                        onResult={({ short, long }) => {
                          if (short) updateLeader(i, "issue", limitWords(short));
                          updateLeader(i, "actionable", long);
                        }}
                        label="Dictate actionable (fills Issue + Actionable)"
                      />
                    </div>
                  </td>
                  <td>
                    <input
                      value={a.partyLeader}
                      onChange={(e) => updateLeader(i, "partyLeader", e.target.value)}
                      placeholder="Name"
                    />
                  </td>
                  <td>
                    <input
                      type="date"
                      value={a.timeline}
                      onChange={(e) => updateLeader(i, "timeline", e.target.value)}
                    />
                  </td>
                  <td className="row-actions">
                    <button type="button" onClick={() => removeLeader(i)} title="Remove">
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className="add-row-btn" onClick={addLeader}>
            + Add Party Leader Actionable
          </button>

          {/* Section 6 */}
          <div className="momf-grid-fields">
            <label>
              Next Meeting (Tentative)
              <input
                type="date"
                name="nextMeetingDate"
                value={form.nextMeetingDate}
                onChange={handleField}
              />
            </label>
          </div>

          {/* Section 7 */}
          <h3 className="momf-section">7. Remarks / Comments / Notes</h3>
          <div className="ta-wrap remarks-wrap">
            <textarea
              className="momf-remarks"
              name="remarks"
              rows={4}
              value={form.remarks}
              onChange={handleField}
              placeholder="Any additional remarks…"
            />
            <MicDictation
              lang={dictationLang}
              context="remarks"
              onResult={({ long }) =>
                setForm((f) => ({ ...f, remarks: appendDictation(f.remarks, long) }))
              }
              label="Dictate remarks"
            />
          </div>

          <button type="submit" className="momf-submit" disabled={submitting}>
            {submitting ? "Saving…" : isEdit ? "Update Meeting" : "Record Meeting"}
          </button>
        </form>
      </div>
    </>
  );
};

export default DynamicNewMom;
